import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import { Text } from "@mantine/core";
import { useModals } from "@mantine/modals";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { BsCheckCircle } from "react-icons/bs";
import Layout from "../../components/shared/Layout/Layout";
import { getGradeList } from "../../actions/Grade.action";
import {
  clearPaymentDue,
  getDuePayments,
} from "../../actions/Payment.action";
import { months, years } from "../../constants/MonthsAndYears";

const cellStyle = {
  padding: "0.5rem",
  verticalAlign: "middle",
};

const centeredCellStyle = {
  ...cellStyle,
  textAlign: "center",
};

const getDuePaymentsList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.payments)) return data.payments;
  if (Array.isArray(data?.duePayments)) return data.duePayments;
  if (Array.isArray(data?.results)) return data.results;
  if (data?.id) return [data];
  if (data && typeof data === "object") {
    return Object.values(data).flat().filter((item) => item && item.id);
  }
  return [];
};

const getPaymentMeta = (meta) => {
  if (!meta) return {};
  if (typeof meta !== "string") return meta;

  try {
    return JSON.parse(meta);
  } catch (error) {
    return {};
  }
};

const normalizePayment = (payment) => {
  const student = payment.student || payment.user || {};
  const meta = getPaymentMeta(payment.meta);

  return {
    ...payment,
    roll:
      payment.roll ||
      payment.uid ||
      payment.studentUid ||
      student.uid ||
      meta.studentUid ||
      "-",
    studentName:
      payment.studentName ||
      payment.name ||
      student.name ||
      meta.studentName ||
      `Student ${payment.userId || payment.id || ""}`.trim(),
    phone: payment.phone || student.phone || meta.phone || "-",
    due_amount:
      payment.due_amount !== null && payment.due_amount !== undefined
        ? payment.due_amount
        : null,
  };
};

const getFormattedAmount = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const parsedValue = parseFloat(value);
  return Number.isNaN(parsedValue) ? "-" : parsedValue.toFixed(2);
};

const DueStudentsPage = ({ grades, getGradeList }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(months[new Date().getMonth()]);
  const [grade, setGrade] = useState("");
  const [shift, setShift] = useState("");
  const [batch, setBatch] = useState("");
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [clearingPaymentId, setClearingPaymentId] = useState(null);
  const modals = useModals();

  useEffect(() => {
    if (grades === null) {
      getGradeList();
    }
  }, [grades, getGradeList]);

  const currentGrade = grades?.find((g) => g.id === parseInt(grade));
  const currentShifts = currentGrade?.shifts || [];
  const currentBatches =
    currentGrade?.batches?.filter((bt) => bt.shiftId === parseInt(shift)) || [];
  const totalDueAmount = payments.reduce((sum, payment) => {
    const parsedAmount = parseFloat(payment.due_amount);
    return sum + (Number.isNaN(parsedAmount) ? 0 : parsedAmount);
  }, 0);
  const columnSums = payments.reduce(
    (acc, payment) => ({
      exam_fee: acc.exam_fee + (parseFloat(payment.exam_fee) || 0),
      extra_amount: acc.extra_amount + (parseFloat(payment.extra_amount) || 0),
      amount: acc.amount + (parseFloat(payment.amount) || 0),
      total_amount:
        acc.total_amount +
        (parseFloat(payment.total_amount || payment.amount) || 0),
      due_amount: acc.due_amount + (parseFloat(payment.due_amount) || 0),
    }),
    {
      exam_fee: 0,
      extra_amount: 0,
      amount: 0,
      total_amount: 0,
      due_amount: 0,
    },
  );

  const selectHandler = async () => {
    setLoading(true);
    setSearched(true);

    const filters = {
      tenant: "school",
      year,
      month,
    };
    if (grade) filters.gradeId = grade;
    if (shift) filters.shiftId = shift;
    if (batch) filters.batchId = batch;

    const data = await getDuePayments(filters);
    setPayments(getDuePaymentsList(data).map(normalizePayment));
    setLoading(false);
  };

  const confirmClearDue = (payment) => {
    modals.openConfirmModal({
      title: "Clear due",
      centered: true,
      children: (
        <Text size="md">
          Are you sure you want to clear due for {payment.studentName} for{" "}
          {payment.month} {payment.year}? The due will be cleared.
        </Text>
      ),
      labels: { confirm: "Clear due", cancel: "Cancel" },
      confirmProps: { color: "green" },
      onCancel: () => {},
      onConfirm: () => handleClearDue(payment),
    });
  };

  const handleClearDue = async (payment) => {
    setClearingPaymentId(payment.id);
    const result = await clearPaymentDue(payment.id);
    setClearingPaymentId(null);

    if (result.success) {
      setPayments((prev) => prev.filter((item) => item.id !== payment.id));
      toast.success("Due cleared successfully");
      return;
    }

    toast.error(result.error || "Error clearing due");
  };

  const renderClearDueButton = (payment) => (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id={`clear-due-${payment.id}`}>Clear due</Tooltip>}
    >
      <span className="d-inline-block">
        <Button
          variant="outline-success"
          size="sm"
          aria-label="Clear due"
          onClick={() => confirmClearDue(payment)}
          disabled={clearingPaymentId === payment.id}
          style={{ lineHeight: 1 }}
        >
          {clearingPaymentId === payment.id ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <BsCheckCircle />
          )}
        </Button>
      </span>
    </OverlayTrigger>
  );

  return (
    <div className="bg_light">
      <Layout title="Due Students">
        <Container className="pb-4">
          <Card bg="white" text="dark" className="crd shadow mb-4">
            <Card.Body>
              <Row>
                <Col md={3} className="py-3">
                  <label htmlFor="year" className="d-block pb-2">
                    Year
                  </label>
                  <Form.Select
                    id="year"
                    name="year"
                    value={year}
                    onChange={(e) => setYear(parseInt(e.target.value))}
                  >
                    <option value={""}>Select Year</option>
                    {years.map((y, i) => (
                      <option key={i} value={y}>
                        {y}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3} className="py-3">
                  <label htmlFor="month" className="d-block pb-2">
                    Month
                  </label>
                  <Form.Select
                    id="month"
                    name="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  >
                    <option value={""}>Select Month</option>
                    {months.map((m, i) => (
                      <option key={i} value={m}>
                        {m}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3} className="py-3">
                  <label htmlFor="grade" className="d-block pb-2">
                    Class
                  </label>
                  <Form.Select
                    id="grade"
                    value={grade}
                    onChange={(e) => {
                      setBatch("");
                      setShift("");
                      setGrade(e.target.value);
                    }}
                  >
                    <option value={""}>Select Class</option>
                    {grades &&
                      grades.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                  </Form.Select>
                </Col>
                {grade !== "" ? (
                  <Col md={3} className="py-3">
                    <label htmlFor="shift" className="d-block pb-2">
                      Shift
                    </label>
                    <Form.Select
                      id="shift"
                      value={shift}
                      onChange={(e) => {
                        setBatch("");
                        setShift(e.target.value);
                      }}
                    >
                      <option value={""}>Select Shift</option>
                      {currentShifts.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                ) : (
                  <></>
                )}
              </Row>
              <Row>
                {grade !== "" && shift !== "" ? (
                  <Col md={3} className="py-3">
                    <label htmlFor="batch" className="d-block pb-2">
                      Batch
                    </label>
                    <Form.Select
                      id="batch"
                      value={batch}
                      onChange={(e) => setBatch(e.target.value)}
                    >
                      <option value={""}>Select Batch</option>
                      {currentBatches.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                ) : (
                  <></>
                )}
                <Col
                  md={grade !== "" && shift !== "" ? 9 : 12}
                  className="d-flex justify-content-end align-items-end py-3"
                >
                  <Button
                    onClick={selectHandler}
                    variant="primary"
                    className="btn_primary"
                    disabled={loading || !year || !month}
                  >
                    {loading ? "Loading..." : "Select"}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="crd p-md-4 pb-md-0 p-2">
            <Card.Body>
              <h5 className="mb-3">Due Payments</h5>
              {loading ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: 300 }}
                >
                  <Spinner variant="dark" animation="grow" />
                </div>
              ) : payments.length === 0 ? (
                <span className="text-center fs-5 d-block pb-3">
                  {searched
                    ? "No due payments found."
                    : "Select filters and click Select."}
                </span>
              ) : (
                <>
                  <div className="mb-3">
                    <strong>Total Due Amount: {totalDueAmount.toFixed(2)}</strong>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <Table
                      striped
                      bordered
                      hover
                      responsive
                      size="sm"
                      style={{ fontSize: "0.875rem" }}
                    >
                      <thead>
                        <tr>
                          <th style={cellStyle}>Roll</th>
                          <th style={cellStyle}>Name</th>
                          <th style={cellStyle}>Phone</th>
                          <th style={cellStyle}>Admission Fee/ Exam Fee</th>
                          <th style={cellStyle}>Session Charge/ Extra Cost</th>
                          <th style={cellStyle}>Service Charge</th>
                          <th style={cellStyle}>Total</th>
                          <th style={cellStyle}>Due Amount</th>
                          <th style={cellStyle}>Month</th>
                          <th style={cellStyle}>Year</th>
                          <th style={centeredCellStyle}>Due</th>
                          <th style={centeredCellStyle}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((payment, index) => (
                          <tr key={payment.id || index}>
                            <td style={cellStyle}>{payment.roll}</td>
                            <td style={cellStyle}>{payment.studentName}</td>
                            <td style={cellStyle}>{payment.phone}</td>
                            <td style={cellStyle}>
                              {parseFloat(payment.exam_fee || 0).toFixed(2)}
                            </td>
                            <td style={cellStyle}>
                              {parseFloat(payment.extra_amount || 0).toFixed(2)}
                            </td>
                            <td style={cellStyle}>
                              {parseFloat(payment.amount || 0).toFixed(2)}
                            </td>
                            <td style={cellStyle}>
                              {parseFloat(
                                payment.total_amount || payment.amount || 0,
                              ).toFixed(2)}
                            </td>
                            <td style={cellStyle}>
                              {getFormattedAmount(payment.due_amount)}
                            </td>
                            <td style={cellStyle}>{payment.month}</td>
                            <td style={cellStyle}>{payment.year}</td>
                            <td style={centeredCellStyle}>
                              <span className="badge bg-warning text-dark">
                                Due
                              </span>
                            </td>
                            <td style={centeredCellStyle}>
                              {renderClearDueButton(payment)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="table-secondary fw-bold">
                          <td
                            colSpan={3}
                            style={{ ...cellStyle, textAlign: "right" }}
                          >
                            Total
                          </td>
                          <td style={cellStyle}>
                            {columnSums.exam_fee.toFixed(2)}
                          </td>
                          <td style={cellStyle}>
                            {columnSums.extra_amount.toFixed(2)}
                          </td>
                          <td style={cellStyle}>
                            {columnSums.amount.toFixed(2)}
                          </td>
                          <td style={cellStyle}>
                            {columnSums.total_amount.toFixed(2)}
                          </td>
                          <td style={cellStyle}>
                            {columnSums.due_amount.toFixed(2)}
                          </td>
                          <td colSpan={4} style={cellStyle} />
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Container>
      </Layout>
    </div>
  );
};

const mapStateToProps = (state) => ({
  grades: state.grade.grade,
});

export default connect(mapStateToProps, { getGradeList })(DueStudentsPage);
